/* AppBar — the approved mock's app bar (design/mock-13 `.appbar`). Purple is identity only:
 * the wordmark and the current pill's text. */

.appbar {
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sage);
}

.inner {
  max-width: var(--shell-max);
  margin: 0 auto;
  padding: 10px var(--shell-gutter);
  display: flex;
  align-items: center;
  gap: 28px;
}

/* ── Brand ── */

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  color: inherit;
  flex: none;
  border-radius: 10px;
}

.brand:hover {
  text-decoration: none;
}

.logo {
  height: 30px;
  width: auto;
  display: block;
}

.wordmark {
  font-family: var(--serif);
  font-weight: 500;
  font-size: 19px;
  color: var(--purple-dark);
  letter-spacing: -0.005em;
}

.product {
  font-size: 12px;
  color: var(--gray-light);
  font-weight: 600;
  margin-left: 2px;
}

/* ── Surfaces ── */

.surfaces {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.surface,
.more {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--gray);
  padding: 7px 15px;
  border: none;
  border-radius: var(--radius-full);
  background: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.surface:hover,
.more:hover,
.more[data-hovered] {
  background: var(--lavender-light);
  color: var(--purple-dark);
  text-decoration: none;
}

.surface[aria-current='page'],
.more[data-current],
.more[data-pressed] {
  background: var(--lavender);
  color: var(--purple-dark);
}

.more[data-focus-visible] {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
}

/* "More" is quieter than the surfaces: lighter until it holds the current page. */
.more:not([data-current]):not([data-pressed]):not(:hover) {
  color: var(--gray-light);
  font-weight: 500;
}

/* The current More page's name; "More" replaces it at phone width (ISS-197). */
.moreShort {
  display: none;
}

.chev {
  width: 13px;
  height: 13px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* ── 13.2-11: the pinned pages (Registers, "+ Lodge"), after More, quieter than the surfaces ── */

.divider {
  flex: none;
  width: 1px;
  height: 20px;
  margin: 0 8px;
  background: var(--sage);
}

.pinned {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 500;
  color: var(--gray);
  padding: 7px 14px;
  border: 1px solid transparent;
  border-radius: var(--radius-full);
  white-space: nowrap;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.pinned:hover {
  background: var(--lavender-light);
  color: var(--purple-dark);
  text-decoration: none;
}

.pinned[aria-current='page'] {
  background: var(--lavender);
  color: var(--purple-dark);
  font-weight: 600;
}

/* Lodge starts something, so it reads as an action: a quiet outline and a plus. */
.pinnedAction {
  border-color: var(--sage-dark);
}

.pinnedAction[aria-current='page'] {
  border-color: transparent;
}

.plus {
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
}

.count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--radius-full);
  background: var(--white);
  border: 1px solid var(--sage-dark);
  font-size: 11.5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--charcoal);
  font-variant-numeric: tabular-nums;
}

/* ── Person ── */

.me {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  font: inherit;
  font-size: 13px;
  color: var(--gray);
  background: none;
  border: none;
  padding: 3px 3px 3px 10px;
  border-radius: var(--radius-full);
  text-align: right;
  cursor: pointer;
  flex: none;
  transition: background 0.2s;
}

.me[data-hovered],
.me[data-pressed] {
  background: var(--lavender-light);
}

.me[data-focus-visible] {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
}

.who b {
  display: block;
  color: var(--charcoal);
  font-weight: 600;
  font-size: 13.5px;
  line-height: 1.2;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--sage);
  color: var(--purple-dark);
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Menus (More, account) ── */

.popover {
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  min-width: 220px;
  padding: 6px;
  outline: none;
}

.popover[data-entering] {
  animation: hap-rise 0.18s var(--ease);
}

.menu {
  outline: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.menuItem {
  display: block;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 14px;
  color: var(--charcoal);
  cursor: pointer;
  outline: none;
  text-decoration: none;
}

.menuItem[data-focused],
.menuItem[data-hovered] {
  background: var(--lavender-light);
  color: var(--purple-dark);
  text-decoration: none;
}

.menuItem[data-focus-visible] {
  box-shadow: inset 0 0 0 2px var(--purple);
}

.menuItem[data-current] {
  color: var(--purple-dark);
  font-weight: 600;
}

.menuHeader {
  padding: 8px 12px 6px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--charcoal);
}

.menuHeader span {
  display: block;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--gray);
}

.sep {
  height: 1px;
  background: var(--line);
  margin: 4px 6px;
  border: none;
}

/* ── Responsive (the mock's breakpoints) ── */

@media (max-width: 1100px) {
  .inner {
    gap: 18px;
  }

  .who {
    display: none;
  }
}

@media (max-width: 720px) {
  .product {
    display: none;
  }

  .inner {
    gap: 10px;
  }

  .wordmark {
    display: none;
  }

  .surfaces {
    overflow-x: auto;
    scrollbar-width: none;
  }

  .surface,
  .more {
    padding: 7px 11px;
    font-size: 13.5px;
  }
}

/* Phone: four calm words fit without the pills (the counts return at 560 px and up). */
@media (max-width: 560px) {
  .count {
    display: none;
  }

  .surfaces {
    gap: 2px;
  }

  .surface,
  .more {
    padding: 7px 8px;
    gap: 4px;
  }

  .me {
    padding: 0;
  }

  .inner {
    gap: 6px;
  }

  /* A long page name would be clipped by the scrolling pills; the pill stays highlighted. */
  .moreLabel {
    display: none;
  }

  .moreShort {
    display: inline;
  }
}
